import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Coins } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useInitialTokenAmount } from "./hooks/useInitialTokenAmount";
import { TokenAmountDisplay } from "./components/TokenAmountDisplay";
import { TokenAmountEditor } from "./components/TokenAmountEditor";

export const InitialTokenAmount = () => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [amount, setAmount] = useState("");
  
  const { initialAmount, isLoading, fetchError, updateMutation } = useInitialTokenAmount();

  // Set initial amount when data is loaded
  if (initialAmount && !amount) {
    setAmount(initialAmount);
  }

  const handleSave = () => {
    if (!amount || isNaN(Number(amount))) {
      toast({
        title: "Error",
        description: "Please enter a valid number",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutate(amount, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Initial token amount updated successfully",
        });
        setIsEditing(false);
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update initial token amount",
          variant: "destructive",
        });
      },
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAmount(initialAmount || "");
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (fetchError) {
    return (
      <div className="text-red-500">
        Error loading initial token amount: {fetchError.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Coins className="h-6 w-6" />
        Initial Token Amount
      </h2>
      <Card className="p-6">
        {isEditing ? (
          <TokenAmountEditor
            amount={amount}
            isLoading={updateMutation.isPending}
            onAmountChange={setAmount}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          <TokenAmountDisplay
            amount={initialAmount || ""}
            onEdit={() => setIsEditing(true)}
          />
        )}
        <p className="text-muted-foreground mt-2">
          Number of tokens given to new users upon registration
        </p>
      </Card>
    </div>
  );
};